import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Checkout() {
  const [params] = useSearchParams();
  const { user } = useAuth();
  const [form, setForm] = useState({
    customer_first_name: user?.first_name || "",
    customer_last_name: user?.last_name || "",
    customer_email: user?.email || "",
    customer_phone: "",
    vehicle_registration: "",
    vehicle_make: "",
    vehicle_model: "",
    vehicle_colour: "",
    flight_number: "",
    terminal: "",
  });
  const [agree, setAgree] = useState(false);
  const [err, setErr] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const total = parseFloat(params.get("total_price") || "0");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!agree) return setErr("You must agree to the booking terms");
    setSubmitting(true);
    try {
      const bookingPayload = {
        offer_id: params.get("offer_id"),
        canonical_parking_id: params.get("canonical_parking_id"),
        airport_code: params.get("airport_code"),
        dropoff: params.get("dropoff"),
        pickup: params.get("pickup"),
        total_price: total,
        parking_name: params.get("name"),
        category: params.get("category"),
        provider_id: params.get("provider_id"),
        ...form,
      };
      const bk = await api.post("/bookings", bookingPayload);
      const ck = await api.post("/payments/checkout", {
        booking_id: bk.data.id,
        origin_url: window.location.origin,
      });
      window.location.href = ck.data.checkout_url;
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Could not start payment");
      setSubmitting(false);
    }
  };

  const upd = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  return (
    <div className="max-w-4xl mx-auto px-5 lg:px-8 py-10">
      <h1 className="font-display text-3xl font-semibold text-[var(--fpn-navy)]" data-testid="checkout-title">Checkout</h1>
      <p className="text-sm text-slate-600 mt-1">{params.get("name")} — £{total.toFixed(2)}</p>

      <form onSubmit={submit} className="mt-8 grid gap-6" data-testid="checkout-form">
        <div>
          <div className="text-xs font-bold tracking-[0.18em] uppercase text-slate-500 mb-3">Contact</div>
          <div className="grid md:grid-cols-2 gap-4">
            <input required placeholder="First name" value={form.customer_first_name} onChange={upd("customer_first_name")} className="border border-slate-200 rounded-lg p-3 text-sm" data-testid="checkout-first-name"/>
            <input required placeholder="Last name" value={form.customer_last_name} onChange={upd("customer_last_name")} className="border border-slate-200 rounded-lg p-3 text-sm" data-testid="checkout-last-name"/>
            <input required type="email" placeholder="Email" value={form.customer_email} onChange={upd("customer_email")} className="border border-slate-200 rounded-lg p-3 text-sm" data-testid="checkout-email"/>
            <input required placeholder="Phone" value={form.customer_phone} onChange={upd("customer_phone")} className="border border-slate-200 rounded-lg p-3 text-sm" data-testid="checkout-phone"/>
          </div>
        </div>
        <div>
          <div className="text-xs font-bold tracking-[0.18em] uppercase text-slate-500 mb-3">Vehicle</div>
          <div className="grid md:grid-cols-2 gap-4">
            <input required placeholder="Registration" value={form.vehicle_registration} onChange={upd("vehicle_registration")} className="border border-slate-200 rounded-lg p-3 text-sm" data-testid="checkout-vehicle-reg"/>
            <input required placeholder="Make" value={form.vehicle_make} onChange={upd("vehicle_make")} className="border border-slate-200 rounded-lg p-3 text-sm" data-testid="checkout-vehicle-make"/>
            <input required placeholder="Model" value={form.vehicle_model} onChange={upd("vehicle_model")} className="border border-slate-200 rounded-lg p-3 text-sm" data-testid="checkout-vehicle-model"/>
            <input required placeholder="Colour" value={form.vehicle_colour} onChange={upd("vehicle_colour")} className="border border-slate-200 rounded-lg p-3 text-sm" data-testid="checkout-vehicle-colour"/>
            <input placeholder="Flight number (optional)" value={form.flight_number} onChange={upd("flight_number")} className="border border-slate-200 rounded-lg p-3 text-sm" data-testid="checkout-flight"/>
            <input placeholder="Terminal (optional)" value={form.terminal} onChange={upd("terminal")} className="border border-slate-200 rounded-lg p-3 text-sm" data-testid="checkout-terminal"/>
          </div>
        </div>
        <label className="flex items-start gap-3 text-sm text-slate-700" data-testid="checkout-agree">
          <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="mt-1"/>
          I agree to the Parkpot booking terms and cancellation policy. Payment is processed securely by Stripe. Apple Pay and Google Pay are supported.
        </label>
        {err && <div className="text-sm text-red-600" data-testid="checkout-error">{err}</div>}
        <button disabled={submitting} className="btn-amber rounded-full py-3 px-6 text-sm w-fit" data-testid="checkout-confirm-button">
          {submitting ? "Redirecting to Stripe…" : `Continue to payment · £${total.toFixed(2)}`}
        </button>
        <p className="text-[11px] text-slate-500">You'll be taken to Stripe's secure checkout to complete payment.</p>
      </form>
    </div>
  );
}
